import {
  HttpClient,
  HttpErrorResponse
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";
import {
  Observable,
  throwError
} from "rxjs";
import {
  catchError,
  distinctUntilChanged,
  map,
  take,
  tap
} from "rxjs/operators";
import { ToastAlertService } from "src/app/shared/utils/toast.service";
import {
  ProgramOfficeInformationModel, ServiceProviderProfileCompleteness
} from "../domain";
import { ApiEndpointService } from "./api-endpoint.service";
import * as ProfileActions from "src/app/core/state/profile/profile.action";
import * as ProgramOfficeDashboardAction from 'src/app/core/state/program-office-dashboard/program-office.dashboard.action';
import { getProfileData } from "../state/profile";
import { IProfileDetails } from "../domain/profile.model";
import { GlobalService } from "./global.service";
import { AuthService } from "./auth.service";
import { getBrandDetails } from "../state/global";
import { httpFaultMessage } from 'src/app/shared/utils/http-fault.util';

@Injectable({
  providedIn: "root"
})
export class ProgramOfficeService {
  profileData: IProfileDetails;
  /**
   * Constructor.
   */
  constructor(private http: HttpClient, private toastService: ToastAlertService, private store: Store, private globalService: GlobalService, private authService: AuthService) {
    this.store.pipe(select(getProfileData))
      .pipe(distinctUntilChanged()).subscribe((res) => {
        if (res) {
          this.profileData = res;
        }
      })
  }

  getProfileCompleteness() {
    return this.store.dispatch(new ProgramOfficeDashboardAction.GetProfileCompleteness());
  }

  public patchProgramOfficeInfo(programOfficeInfo: ProgramOfficeInformationModel): Observable<any> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.PROGRAM_OFFICE_INFO);
    return this.http.patch(url, programOfficeInfo).pipe(
      tap(() => {
        this.getProfileCompleteness()
      }),
      catchError((fault: HttpErrorResponse) => {
        console.warn(`loginFault( ${fault.error?.message || fault.message || fault.status} )`);
        this.toastService.showToast(this.globalService.getErrorMessage(fault, 'Error while saving ProgramOffice info!'), 'error');
        return throwError(() => fault);
      })
    );
  }



  public patchEngagementInfo(programOfficeInfo: ProgramOfficeInformationModel): Observable<any> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.ENGAGEMENT_INFO);
    return this.http.patch(url, programOfficeInfo).pipe(
      tap(() => {
        this.getProfileCompleteness()
      }),
      catchError((fault: HttpErrorResponse) => {
        console.warn(`loginFault( ${fault.error?.message || fault.message || fault.status} )`);
        this.toastService.showToast(this.globalService.getErrorMessage(fault, 'Error while saving engagement info!'), 'error');
        return throwError(() => fault);
      })
    );
  }


  public getProgramOfficeProfileCompleteness(): Observable<ServiceProviderProfileCompleteness> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.PROGRAM_OFFICE_PROFILE_COMPLETENESS);
    return this.http.get(url).pipe(
      map((response: any) => {
        return response.data;
      }),
      tap((res: any) => {
        if (res?.isRejected) {
          setTimeout(() => {

            this.store.pipe(select(getBrandDetails)).pipe(take(1)).subscribe((res) => {
              if (res) {
                if (res.features?.logout_on_rejection) {
                  this.toastService.showToast('Your profile is rejected', 'error');
                  this.authService.logout();
                }
              }
            })

          }, 1000)
        }
      }),
      catchError((fault: HttpErrorResponse) => {
        console.warn(`getIndividualInvestorCompleteness( ${httpFaultMessage(fault, `HTTP ${fault?.status}`)} )`);
        return throwError(() => fault);
      })
    );
  }

  public getProgramOfficeInfo(): Observable<any> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.PROGRAM_OFFICE_INFO);
    return this.http.get(url).pipe(
      catchError((fault: HttpErrorResponse) => {
        return throwError(() => fault);
      })
    );
  }

  public uploadLogo(logo: File, refetchProfile = false): Observable<any> {
    const payload = new FormData();
    payload.append('file', logo);
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.PROGRAM_OFFICE_LOGO_UPLOAD);
    console.info(`coporate up logo`, payload);
    return this.http.post(url, payload).pipe(
      tap(() => {
        if (refetchProfile) {
          this.store.dispatch(new ProfileActions.GetProfile());
        }
        this.getProfileCompleteness()
      }),
      catchError((fault: HttpErrorResponse) => {
        this.toastService.showToast(this.globalService.getErrorMessage(fault, 'Error while uploading logo!'), 'error');
        console.warn(`save coporate Uplogo( ${httpFaultMessage(fault, `HTTP ${fault?.status}`)} )`);
        return throwError(() => fault);
      })
    );
  }

  public sendApprovalRequest(): Observable<any> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.PROGRAM_OFFICE_DASHBOARD_APPROVAL);
    return this.http.patch(url, {}).pipe(
      tap(() => this.getProfileCompleteness()),
      map((response: any) => {
        return response.message;
      }),
      catchError((fault: HttpErrorResponse) => {
        console.warn(`sendApproval( ${httpFaultMessage(fault, `HTTP ${fault?.status}`)} )`);
        return throwError(() => fault);
      })
    );
  }

  public getProgramOfficePublicProfileData(programOfficeUUID: string): Observable<any> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.PROGRAM_OFFICE_PUBLIC_PROFILE + `/${programOfficeUUID}`);
    return this.http.get(url).pipe(
      map((response: any) => {
        return response?.data;
      }),
      catchError((fault: HttpErrorResponse) => {
        this.toastService.showToast(fault?.error?.message || 'Error while getting ProgramOffice public profile data', 'error');
        return throwError(() => fault);
      })
    );
  }

  public getProgramOfficeDashboard(): Observable<any> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.PROGRAM_OFFICE_DASHBOARD);
    return this.http.get(url).pipe(
      map((response: any) => {
        return response.data;
      }),
      catchError((fault: HttpErrorResponse) => {
        this.toastService.showToast(this.globalService.getErrorMessage(fault, 'Error while fetching dashboard details!'), 'error');
        return throwError(() => fault);
      })
    );
  }

public onSubmitDocuments(payload: any): Observable<any> {
  const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.APPLICATION_PROGRAMS_MANAGEMENT) +'submit-document';

  return this.http.post(url, payload).pipe(
    map((response: any) => response),
    catchError((fault: HttpErrorResponse) => throwError(() => fault))
  );
}

public uploadProgramDocumentOnS3(programUUID: string, file: File): Observable<any> {
  const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.APPLICATION_PROGRAMS_MANAGEMENT) + programUUID +'/upload-document';
  const formData = new FormData();
  
  formData.append('file', file); 

  return this.http.post(url, formData).pipe( 
    map((response: any) => response),
    catchError((fault: HttpErrorResponse) => throwError(() => fault))
  );
}

public getPreDocumentsData(programId:number,submissionId:number,roundId?:number): Observable<any> {
  let roundID= roundId ? "/"+ roundId:''
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.APPLICATION_PROGRAMS_MANAGEMENT)+ "fetch-documents/"+programId + "/" +submissionId + roundID;
    return this.http.get(url).pipe(
      map((response: any) => {
        return response.data;
      }),
      catchError((fault: HttpErrorResponse) => {
        this.toastService.showToast(this.globalService.getErrorMessage(fault, 'Error while fetching dashboard details!'), 'error');
        return throwError(() => fault);
      })
    );
  }

public getRejectedDocumentsData(programId:number,submissionId:number): Observable<any> {

    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.APPLICATION_PROGRAMS_MANAGEMENT)+ "fetch-documents-rejected/"+ programId + "/" + submissionId ;
    return this.http.get(url).pipe(
      map((response: any) => {
        return response.data;
      }),
      catchError((fault: HttpErrorResponse) => {
        this.toastService.showToast(this.globalService.getErrorMessage(fault, 'Error while fetching dashboard details!'), 'error');
        return throwError(() => fault);
      })
    );
  }

  public updateDocument(documentId: number, payload: any): Observable<any> {
  const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.APPLICATION_PROGRAMS_MANAGEMENT) +"update-document/" +documentId;

  return this.http.put(url, payload).pipe(
    map((response: any) => response.data),
    catchError((error: HttpErrorResponse) => {
      return throwError(() => error);
    })
  );
}
public deleteDocument(documentId: number): Observable<any> {
  const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.APPLICATION_PROGRAMS_MANAGEMENT)+"delete-document/" + documentId;

  return this.http.delete(url).pipe(
    map((response: any) => response ),
    catchError((error: HttpErrorResponse) => {
      return throwError(() => error);
    })
  );
}
  public userData(): Observable<any> {
  const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.USER)+ "profile";

  return this.http.get(url).pipe(
    map((response: any) => response.data),
    catchError((error: HttpErrorResponse) => {
      return throwError(() => error);
    })
  );
}
public uploadPitchDeck3(submissionId, file: File): Observable<any> {
  const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.FORMS_MANAGEMENT) + "submission/"+submissionId +'/cfa/upload-pitch-document';
  const formData = new FormData();
  
  formData.append('file', file); 

  return this.http.post(url, formData).pipe( 
    map((response: any) => response),
    catchError((fault: HttpErrorResponse) => throwError(() => fault))
  );
}


public getFeedbackQuestions(): Observable<any> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.MEETING_FEED_BACK)+ "feedback-questions/startup";
    return this.http.get(url).pipe(
      map((response: any) => {
        return response.data;
      }),
      catchError((fault: HttpErrorResponse) => {
        this.toastService.showToast(this.globalService.getErrorMessage(fault, 'Error while fetching dashboard details!'), 'error');
        return throwError(() => fault);
      })
    );
  }



  public onSubmitFeedback(payload: any,uuid:any): Observable<any> {
  const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.MEETING_FEED_BACK) +uuid+"/submit-feedback";

  return this.http.post(url, payload).pipe(
    map((response: any) => response),
    catchError((fault: HttpErrorResponse) => throwError(() => fault))
  );
}



public getMeetingDetails(meetingUuid): Observable<any> {
 
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.MEETING_FEED_BACK)+ meetingUuid;
    return this.http.get(url).pipe(
      map((response: any) => {
       
        return response.data;
      }),
      catchError((fault: HttpErrorResponse) => {
      
        this.toastService.showToast(this.globalService.getErrorMessage(fault, 'Error while fetching dashboard details!'), 'error');
        return throwError(() => fault);
      })
    );
  }




  public getSubmittedDetails(meetingId,email): Observable<any> {

 
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.MEETING_FEED_BACK)+ 'feedback-submitted/'+meetingId +'/'+email;
    return this.http.get(url).pipe(
      map((response: any) => {
       
        return response;
      }),
      catchError((fault: HttpErrorResponse) => {
        return throwError(() => fault);
      })
    );
  }

  public getMembershipData(moduleType:string,programId: number,roundId?:number): Observable<any> {
  const PROGRAM_DYNMIC_URL=  moduleType=='application_programs'? ApiEndpointService.ENDPOINT.APPLICATION_PROGRAMS_MANAGEMENT:ApiEndpointService.ENDPOINT.PROGRAMS_MANAGEMENT
  const baseUrl = ApiEndpointService.getEndpoint(PROGRAM_DYNMIC_URL);
  const url = `${baseUrl}membership-types/${programId}/${roundId ?? ''}`;

  return this.http.get<any>(url).pipe(
    map(response => response.data ?? []),
    catchError((error: HttpErrorResponse) => throwError(() => error))
  );
}

  public getPrimaryEventData(): Observable<any> {
  const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.PRIMARY_EVENT,true);
  return this.http.get<any>(url).pipe(
    map(response => response.data ?? []),
    catchError((error: HttpErrorResponse) => throwError(() => error))
  );
}

   public getPrimaryEventsBooth(profileType: string, stakeholderId: string): Observable<any> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.PRIMARY_EVENT,true)+ `/booth/${profileType}/${stakeholderId}`;
    return this.http.get(url).pipe(
        map((response: any): any => {
            return response.data || [];
        }),
        catchError((fault: HttpErrorResponse) => {
            return throwError(() => fault);
        })
    );
  }

   public VerifyEmailForCFA(email: string,): Observable<any> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.USER)+ `profile-data/${email}`;
    return this.http.get(url).pipe(
        map((response: any): any => {
            return response.data || [];
        }),
        catchError((fault: HttpErrorResponse) => {
            return throwError(() => fault);
        })
    );
  }

  
  public VerifyEmailForPublicCFA(email: string,): Observable<any> {
    const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.USER)+ `profile-data/guest/${email}`;
    return this.http.get(url).pipe(
        map((response: any): any => {
            return response.data.user || response.data || [];
        }),
        catchError((fault: HttpErrorResponse) => {
            return throwError(() => fault);
        })
    );
  }
}

